// src/Components/Modules/HR/MyRequests.js
import React, { useState, useEffect, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box, Typography, Paper, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Tooltip, Skeleton, Alert, Stack, Avatar, Tabs, Tab
} from "@mui/material";
import {
  FaCalendarAlt, FaMoneyBillWave, FaBoxOpen, FaHandHoldingUsd,
  FaCheckCircle, FaTimesCircle, FaClock, FaInfoCircle, FaEye, FaFilter
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const StatCard = styled(Card)(({ theme, color }) => ({
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2.5),
  textAlign: "center",
  border: `1px solid ${color}30`,
  background: `${color}08`,
  transition: "all 0.3s ease",
  "&:hover": { transform: "translateY(-4px)", boxShadow: `0 8px 20px ${color}20` },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": { backgroundColor: theme.palette.action.hover },
  transition: "background-color 0.2s ease",
}));

const MyRequests = () => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchAllRequests();
  }, []);

  const fetchAllRequests = async () => {
    setLoading(true);
    try {
      const [leaveRes, expenseRes, loanRes, reqRes] = await Promise.all([
        AxiosAuthInstance.get("hr/leave/my-leaves").catch(() => ({ data: [] })),
        AxiosAuthInstance.get("expenses").catch(() => ({ data: [] })),
        AxiosAuthInstance.get("hr/loans").catch(() => ({ data: [] })),
        AxiosAuthInstance.get("hr/requisitions/my").catch(() => ({ data: [] })),
      ]);

      const formattedLeave = (leaveRes.data || []).map(item => ({
        id: item.id,
        type: "Leave",
        date: item.created_at,
        details: `${item.leave_type?.name || "Leave"} (${item.days_count} days)`,
        status: item.status,
        rejectionReason: item.rejected_reason || item.rejection_reason || null,
        icon: FaCalendarAlt,
        color: "#2196f3"
      }));

      const formattedExpense = (expenseRes.data || []).map(item => ({
        id: item.id,
        type: "Expense",
        date: item.created_at,
        details: `${item.category} - ৳${Number(item.amount).toLocaleString()}`,
        status: item.status,
        rejectionReason: item.rejected_reason || item.rejection_reason || null,
        icon: FaMoneyBillWave,
        color: "#4caf50"
      }));

      const formattedLoan = (loanRes.data || []).map(item => ({
        id: item.id,
        type: "Loan",
        date: item.created_at,
        details: `${item.type === 'advance_salary' ? 'Advance Salary' : 'Loan'} - ৳${Number(item.amount).toLocaleString()}`,
        status: item.status,
        rejectionReason: item.rejection_reason || item.rejection_reason || null,
        icon: FaHandHoldingUsd,
        color: "#ff9800"
      }));

      const formattedReq = (reqRes.data.data || reqRes.data || []).map(item => ({
        id: item.id,
        type: "Requisition",
        date: item.created_at,
        details: `${item.item_name} (Qty: ${item.requested_quantity})`,
        status: item.status,
        rejectionReason: item.rejection_reason || item.rejection_reason || null,
        icon: FaBoxOpen,
        color: "#9c27b0"
      }));

      const allRequests = [
        ...formattedLeave,
        ...formattedExpense,
        ...formattedLoan,
        ...formattedReq
      ].sort((a, b) => new Date(b.date) - new Date(a.date));

      setRequests(allRequests);
    } catch (err) {
      console.error("Failed to fetch requests", err);
      Swal.fire({ icon: "error", title: "Error", text: "Failed to load requests." });
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = useMemo(() => {
    if (filter === "all") return requests;
    return requests.filter(r => r.type.toLowerCase() === filter.toLowerCase());
  }, [requests, filter]);

  const stats = useMemo(() => {
    return {
      total: requests.length,
      pending: requests.filter(r => r.status === "pending").length,
      approved: requests.filter(r => ["approved", "fulfilled", "repaying", "closed"].includes(r.status?.toLowerCase())).length,
      rejected: requests.filter(r => r.status === "rejected").length,
    };
  }, [requests]);

  const getStatusChip = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "pending") {
      return <Chip icon={<FaClock style={{fontSize: 12}} />} label="Pending" size="small" sx={{ bgcolor: "#fff3e0", color: "#ef6c00", fontWeight: 600 }} />;
    }
    if (["approved", "fulfilled", "repaying", "closed"].includes(statusLower)) {
      return <Chip icon={<FaCheckCircle style={{fontSize: 12}} />} label={statusLower === "closed" ? "Closed" : "Approved"} size="small" sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 }} />;
    }
    if (statusLower === "rejected") {
      return <Chip icon={<FaTimesCircle style={{fontSize: 12}} />} label="Rejected" size="small" sx={{ bgcolor: "#ffebee", color: "#c62828", fontWeight: 600 }} />;
    }
    return <Chip label={status} size="small" sx={{ bgcolor: "#f5f5f5", color: "#757575" }} />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
        <Breadcrumb title="My Requests" />
        <GradientPaper><Skeleton variant="text" width="40%" height={50} /><Skeleton variant="text" width="30%" /></GradientPaper>
        <Grid container spacing={3}>{[1, 2, 3, 4].map((i) => <Grid item xs={12} sm={6} md={3} key={i}><Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} /></Grid>)}</Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="My Requests" />
      
      {/* Header */}
      <GradientPaper elevation={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaInfoCircle style={{ marginRight: 10, verticalAlign: "middle" }} /> My Requests
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>Track all your leave, expense, loan, and requisition requests in one place.</Typography>
          </Box>
        </Box>
      </GradientPaper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard color="#2196f3">
            <Typography variant="h3" fontWeight={700} color="#1565c0">{stats.total}</Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>Total Requests</Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard color="#ff9800">
            <Typography variant="h3" fontWeight={700} color="#ef6c00">{stats.pending}</Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>Pending</Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard color="#4caf50">
            <Typography variant="h3" fontWeight={700} color="#2e7d32">{stats.approved}</Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>Approved/Fulfilled</Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard color="#f44336">
            <Typography variant="h3" fontWeight={700} color="#c62828">{stats.rejected}</Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>Rejected</Typography>
          </StatCard>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Tabs value={filter} onChange={(e, newValue) => setFilter(newValue)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tab icon={<FaFilter />} iconPosition="start" label="All Requests" value="all" />
          <Tab icon={<FaCalendarAlt />} iconPosition="start" label="Leave" value="leave" />
          <Tab icon={<FaMoneyBillWave />} iconPosition="start" label="Expense" value="expense" />
          <Tab icon={<FaHandHoldingUsd />} iconPosition="start" label="Loan" value="loan" />
          <Tab icon={<FaBoxOpen />} iconPosition="start" label="Requisition" value="requisition" />
        </Tabs>
      </Paper>

      {/* Table */}
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              <StyledTableCell width={80}>SL</StyledTableCell>
              <StyledTableCell>Type</StyledTableCell>
              <StyledTableCell>Details</StyledTableCell>
              <StyledTableCell>Date</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Rejection Reason</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <Box sx={{ textAlign: "center" }}>
                    <FaInfoCircle style={{ fontSize: 48, color: "#bdbdbd", marginBottom: 16 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>No requests found</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredRequests.map((row, index) => {
                const IconComponent = row.icon;
                return (
                  <StyledTableRow key={`${row.type}-${row.id}`} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ bgcolor: `${row.color}20`, color: row.color, width: 36, height: 36 }}>
                          <IconComponent size={16} />
                        </Avatar>
                        <Typography variant="subtitle2" fontWeight={600}>{row.type}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.details}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{formatDate(row.date)}</Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(row.status)}</TableCell>
                    <TableCell>
                      {row.status?.toLowerCase() === "rejected" && row.rejectionReason ? (
                        <Tooltip title={row.rejectionReason}>
                          <Typography variant="body2" color="error" sx={{ 
                            maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "help" 
                          }}>
                            {row.rejectionReason}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.disabled">-</Typography>
                      )}
                    </TableCell>
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </Box>
  );
};

export default MyRequests;